import { useGtag } from 'vue-gtag-next';

const STORAGE_KEY = 'wo_tracked_events';

/**
 * Get tracked events from session storage
 * @returns {Set} Set of tracked event keys
 */
const getTrackedEvents = () => {
    try {
        const tracked = sessionStorage.getItem(STORAGE_KEY);
        return new Set(tracked ? JSON.parse(tracked) : []);
    } catch (error) {
        console.error('Error reading tracked events:', error);
        return new Set();
    }
};

/**
 * Add event to tracked list in session storage
 * @param {string} eventKey - Unique key for the event
 */
const addTrackedEvent = (eventKey) => {
    try {
        const tracked = getTrackedEvents();
        tracked.add(eventKey);
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...tracked]));
    } catch (error) {
        console.error('Error saving tracked event:', error);
    }
};

/**
 * Check if event has been tracked this session
 * @param {string} eventKey - Unique key for the event
 * @returns {boolean} True if already tracked
 */
const isEventTracked = (eventKey) => {
    return getTrackedEvents().has(eventKey);
};

/**
 * Analytics composable for tracking user events
 * Provides methods for tracking resource downloads and audio playback
 * Tracks each unique resource engagement once per session
 */
export function useAnalytics() {
    const { event } = useGtag();

    /**
     * Track resource download events (PDF, ZIP, SIB)
     * Tracks once per session per resource-filetype combination
     * @param {string} title - Resource title
     * @param {string} filetype - File type (pdf, zip, sib)
     */
    const trackDownload = (title, filetype) => {
        if (!event) {
            console.warn('Google Analytics not initialized');
            return;
        }

        const eventKey = `download-${title}-${filetype}`;

        if (isEventTracked(eventKey)) {
            return; // Already tracked this session
        }

        event('resource_engagement', {
            engagement_type: 'download',
            file_type: filetype,
            resource_name: title
        });

        addTrackedEvent(eventKey);
    };

    /**
     * Track audio playback events
     * Tracks once per session per resource
     * @param {string} title - Resource title
     */
    const trackPlay = (title) => {
        if (!event) {
            console.warn('Google Analytics not initialized');
            return;
        }

        const eventKey = `play-${title}`;

        if (isEventTracked(eventKey)) {
            return; // Already tracked this session
        }

        event('resource_engagement', {
            engagement_type: 'play',
            file_type: 'mp3',
            resource_name: title
        });

        addTrackedEvent(eventKey);
    };

    /**
     * Track custom events
     * @param {string} eventName - Event name
     * @param {object} params - Event parameters
     */
    const trackEvent = (eventName, params = {}) => {
        if (!event) {
            console.warn('Google Analytics not initialized');
            return;
        }

        event(eventName, params);
    };

    return {
        trackDownload,
        trackPlay,
        trackEvent
    };
}
